"use client";

import * as React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { trpc } from "@/lib/trpc";
import { Avatar, Button, ListBox, Select, Spinner } from "@heroui/react";
import { NumberTicker, useFullscreen, useTheme } from "@hs/ui";
import { ItemCard, Segment } from "@heroui-pro/react";
import { KPI } from "@heroui-pro/react/kpi";
import { motion } from "motion/react";
import Snowfall from "react-snowfall";
import { IconChevronLeft, IconMaximize as Maximize } from '@tabler/icons-react';

/** activity.config.statisticalPeriods 项 */
interface StatisticalPeriodItem {
  label: string;
  start_time: string;
  end_time: string;
}

const UNIT_ALL_VALUE = "__all__";
const PERIOD_ALL_VALUE = "__all__";

/**
 * 活动统计大屏
 * 路由：/activities/:id/stats
 */
export default function ActivityStatsDashboard() {
  const { id: activityId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // 活动详情（含 config）
  const { data: activity, isLoading: activityLoading } = trpc.activities.get.useQuery(
    { id: activityId! },
    { enabled: !!activityId }
  );

  // 统计周期：从 activity.config.statisticalPeriods 解析
  const periods = React.useMemo((): StatisticalPeriodItem[] => {
    const raw = activity?.config as Record<string, unknown> | null | undefined;
    if (!raw || !Array.isArray(raw.statisticalPeriods)) return [];
    return (raw.statisticalPeriods as StatisticalPeriodItem[]).filter(
      (p) => p?.label && p?.start_time && p?.end_time
    );
  }, [activity?.config]);

  // 当前时间落在哪个周期内，决定默认选中
  const defaultPeriodIndex = React.useMemo(() => {
    const now = Date.now();
    const idx = periods.findIndex((p) => {
      const start = new Date(p.start_time).getTime();
      const end = new Date(p.end_time).getTime();
      return now >= start && now <= end;
    });
    return idx >= 0 ? String(idx) : (periods.length > 0 ? "0" : PERIOD_ALL_VALUE);
  }, [periods]);

  const [unitValue, setUnitValue] = React.useState<string>(UNIT_ALL_VALUE);
  const [periodValue, setPeriodValue] = React.useState<string>(defaultPeriodIndex);

  // 当 periods 或 defaultPeriodIndex 变化时同步 periodValue（仅初始化或周期列表变化时）
  React.useEffect(() => {
    setPeriodValue((prev) => {
      if (periods.length === 0) return PERIOD_ALL_VALUE;
      // prev 是有效的周期索引，保持用户选择
      const idx = periods.findIndex((_, i) => String(i) === prev);
      if (idx >= 0) return prev;
      // prev 无效（包括初始 PERIOD_ALL_VALUE）或不匹配，同步到自动计算的周期
      return defaultPeriodIndex;
    });
  }, [periods.length, defaultPeriodIndex]);

  // BRANCH 组织列表（单位选项）
  const { data: orgs = [], isLoading: orgsLoading } = trpc.orgs.listAll.useQuery(
    { type: "BRANCH" },
    { enabled: !!activityId }
  );

  /** 单位轮播列表：全体 + 各 org，用于每 5 秒自动切换 */
  const unitOptions = React.useMemo(
    () => [UNIT_ALL_VALUE, ...orgs.map((o) => o.id ?? "").filter(Boolean)],
    [orgs]
  );

  // 每 5 秒自动切换到下一个单位，数据会重新请求，排行榜卡片以梯进动画显现
  React.useEffect(() => {
    if (unitOptions.length <= 1) return;
    const id = setInterval(() => {
      setUnitValue((prev) => {
        const idx = unitOptions.indexOf(prev);
        const nextIdx = (idx + 1) % unitOptions.length;
        return unitOptions[nextIdx] ?? UNIT_ALL_VALUE;
      });
    }, 5000);
    return () => clearInterval(id);
  }, [unitOptions]);

  // 统计查询参数（用有效值避免空字符串导致 SQL Bind undefined）
  const effectiveUnit = unitValue || UNIT_ALL_VALUE;
  const effectivePeriod = periodValue || PERIOD_ALL_VALUE;
  const statsParams = React.useMemo(() => {
    const base = {
      activityId: activityId!,
      unitType: effectiveUnit === UNIT_ALL_VALUE ? ("SPACE" as const) : ("ORG" as const),
      ...(effectiveUnit !== UNIT_ALL_VALUE && { unitId: effectiveUnit }),
    };
    const periodIndex = effectivePeriod === "" || effectivePeriod === PERIOD_ALL_VALUE ? -1 : parseInt(effectivePeriod, 10);
    if (periods[periodIndex]) {
      const p = periods[periodIndex];
      return {
        ...base,
        startTime: new Date(p.start_time),
        endTime: new Date(p.end_time),
      };
    }
    return base;
  }, [activityId, effectiveUnit, effectivePeriod, periods]);

  const { data: basicStats, isLoading: statsLoading } = trpc.activities.getBasicStats.useQuery(
    statsParams,
    { enabled: !!activityId }
  );

  const { data: leaderboard, isLoading: leaderboardLoading } =
    trpc.activities.getInvitationLeaderboard.useQuery(
      { ...statsParams, page: 1, pageSize: 10 },
      { enabled: !!activityId }
    );

  const isLoading = activityLoading || orgsLoading || statsLoading || leaderboardLoading;

  // 全屏：下方统计区域支持浏览器全屏
  const fullscreenRef = React.useRef<HTMLDivElement>(null);
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(fullscreenRef, {
    mode: "browser",
  });

  const { theme, setTheme } = useTheme();

  /** 使用特效：空 = 无，snow = 下雪 */
  const [effectValue, setEffectValue] = React.useState<string>("");
  const effectOptions = [{ value: "snow", label: "下雪" }] as const;

  /** 可选主题：light / dark / theme-dark-blue（深蓝暗色） */
  const themeOptions = [
    { value: "light", label: "浅色" },
    { value: "dark", label: "深色" },
    { value: "theme-dark-blue", label: "深蓝" },
  ] as const;
  const currentTheme = theme && themeOptions.some((o) => o.value === theme) ? theme : "light";

  // 倒计时：activity.endTime - 当前时间
  const [countdown, setCountdown] = React.useState<string>("");
  React.useEffect(() => {
    if (!activity?.endTime) {
      setCountdown("");
      return;
    }
    const end = typeof activity.endTime === "string" ? new Date(activity.endTime) : activity.endTime;
    const tick = () => {
      const now = new Date();
      const ms = end.getTime() - now.getTime();
      if (ms <= 0) {
        setCountdown("已结束");
        return;
      }
      const d = Math.floor(ms / (24 * 60 * 60 * 1000));
      const h = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
      const m = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
      const s = Math.floor((ms % (60 * 1000)) / 1000);
      setCountdown(`${d}天${h}时${m}分${s}秒`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [activity?.endTime]);

  if (!activityId) {
    return (
      <div className="p-4 text-muted-foreground">
        缺少活动 ID，请从活动列表进入统计大屏。
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 overflow-y-auto bg-background relative">
      {/* 非全屏时：下雪特效覆盖整页；全屏时在 fullscreenRef 内渲染，否则会随全屏元素一起消失 */}
      {effectValue === "snow" && !isFullscreen && (
        <Snowfall
          style={{
            position: "fixed",
            width: "100vw",
            height: "100vh",
            pointerEvents: "none",
            zIndex: 50,
          }}
        />
      )}
      <header className="border-border flex h-14 shrink-0 items-center border-b">
        <Button isIconOnly variant="tertiary" className="mr-4" onPress={() => navigate("/activities")}>
          <IconChevronLeft className="size-6" stroke={1.5} />
        </Button>
        <h1 className="min-w-0 flex-1 truncate text-2xl font-bold">
          {`${activity?.title ?? "活动"} · 统计大屏`}
        </h1>
        <div className="ml-auto flex items-center gap-2">
          <Select
            className="w-[7.5rem]"
            value={effectValue === "" ? "__none__" : effectValue}
            onChange={(key) => {
              const k = key != null ? String(key) : "__none__";
              setEffectValue(k === "__none__" ? "" : k);
            }}
          >
            <Select.Trigger className="h-9 min-h-9 text-sm">
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                <ListBox.Item id="__none__" textValue="不使用特效">
                  不使用特效
                  <ListBox.ItemIndicator />
                </ListBox.Item>
                {effectOptions.map((opt) => (
                  <ListBox.Item key={opt.value} id={opt.value} textValue={opt.label}>
                    {opt.label}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                ))}
              </ListBox>
            </Select.Popover>
          </Select>
          <Select
            className="w-[7rem]"
            value={currentTheme}
            onChange={(key) => {
              if (key == null) return;
              const k = String(key);
              if (themeOptions.some((o) => o.value === k)) setTheme(k);
            }}
          >
            <Select.Trigger className="h-9 min-h-9 text-sm">
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                {themeOptions.map((opt) => (
                  <ListBox.Item key={opt.value} id={opt.value} textValue={opt.label}>
                    {opt.label}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                ))}
              </ListBox>
            </Select.Popover>
          </Select>
          <Button variant="primary" size="sm" onPress={toggleFullscreen}>
            <Maximize className="mr-1 h-4 w-4" stroke={1.5} />
            全屏播放
          </Button>
        </div>
      </header>

      <div ref={fullscreenRef} className="p-6 mx-auto space-y-6 bg-background relative">
        {/* 全屏时：下雪特效渲染在全屏容器内，随全屏一起显示 */}
        {effectValue === "snow" && isFullscreen && (
          <Snowfall
            style={{
              position: "fixed",
              inset: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              zIndex: 10,
            }}
          />
        )}
        {isLoading && !activity ? (
          <div className="flex justify-center py-12">
            <Spinner className="w-8 h-8" />
          </div>
        ) : (
          <>
            {/* 第一行：活动标题 */}
            <header>
              <h1 className="text-6xl font-bold text-foreground text-center">
                {activity?.title ?? "活动统计大屏"}
              </h1>
            </header>

            {/* 第二行：参数选项（HeroUI Pro Segment，单选、无取消选中） */}
            <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6">
              <div className="flex min-w-0 max-w-full flex-col gap-2 overflow-x-auto">
                <Segment
                  size="lg"
                  selectedKey={unitValue}
                  onSelectionChange={(key) => setUnitValue(String(key))}
                >
                  <Segment.Item id={UNIT_ALL_VALUE}>
                    <Segment.Separator />
                    全体
                  </Segment.Item>
                  {orgs.map((org) => {
                    const oid = org.id ?? "";
                    return (
                      <Segment.Item key={org.id ?? oid} id={oid}>
                        <Segment.Separator />
                        {org.shortName ?? org.fullName ?? "未命名"}
                      </Segment.Item>
                    );
                  })}
                </Segment>
              </div>
              <div className="flex min-w-0 max-w-full flex-col gap-2 overflow-x-auto">
                <Segment
                  size="lg"
                  selectedKey={periodValue}
                  onSelectionChange={(key) => setPeriodValue(String(key))}
                >
                  <Segment.Item id={PERIOD_ALL_VALUE}>
                    <Segment.Separator />
                    不限周期
                  </Segment.Item>
                  {periods.map((p, i) => (
                    <Segment.Item key={i} id={String(i)}>
                      <Segment.Separator />
                      {p.label}
                    </Segment.Item>
                  ))}
                </Segment>
              </div>
            </div>

            {/* 第三行：KPI 统计 + 倒计时；栅格前 3 列等分、最后一列 2fr；标题与数值上下排列 */}
            <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_2fr]">
              <KPI className="h-full min-h-[6.5rem]">
                <KPI.Header>
                  <KPI.Title className="text-xl text-muted-foreground">累计邀请（人）</KPI.Title>
                </KPI.Header>
                <KPI.Content>
                  {basicStats?.participantCount != null ? (
                    <KPI.Value
                      className="text-4xl font-bold text-foreground"
                      maximumFractionDigits={0}
                      style="decimal"
                      value={basicStats.participantCount}
                    />
                  ) : (
                    <span className="text-4xl font-bold text-muted-foreground">–</span>
                  )}
                </KPI.Content>
              </KPI>
              <KPI className="h-full min-h-[6.5rem]">
                <KPI.Header>
                  <KPI.Title className="text-xl text-muted-foreground">活跃伙伴（人）</KPI.Title>
                </KPI.Header>
                <KPI.Content>
                  {basicStats?.inviterCount != null ? (
                    <KPI.Value
                      className="text-4xl font-bold text-foreground"
                      maximumFractionDigits={0}
                      style="decimal"
                      value={basicStats.inviterCount}
                    />
                  ) : (
                    <span className="text-4xl font-bold text-muted-foreground">–</span>
                  )}
                </KPI.Content>
              </KPI>
              <KPI className="h-full min-h-[6.5rem]">
                <KPI.Header>
                  <KPI.Title className="text-xl text-muted-foreground">人均邀请（人）</KPI.Title>
                </KPI.Header>
                <KPI.Content>
                  {basicStats?.averageInvites != null ? (
                    <KPI.Value
                      className="text-4xl font-bold text-foreground"
                      maximumFractionDigits={0}
                      style="decimal"
                      value={basicStats.averageInvites}
                    />
                  ) : (
                    <span className="text-4xl font-bold text-muted-foreground">–</span>
                  )}
                </KPI.Content>
              </KPI>
              <KPI className="h-full min-h-[6.5rem]">
                <KPI.Header>
                  <KPI.Title className="text-xl text-muted-foreground">距离结束</KPI.Title>
                </KPI.Header>
                <KPI.Content>
                  <span className="text-4xl font-semibold text-foreground tabular-nums">
                    {countdown || "–"}
                  </span>
                </KPI.Content>
              </KPI>
            </div>

            {/* 邀请排行榜：两列各 50%，行内为 HeroUI Pro ItemCard */}
            <section>
              <h2 className="text-lg font-semibold text-foreground mb-4">邀请排行榜</h2>
              {leaderboardLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner className="w-6 h-6" />
                </div>
              ) : leaderboard && leaderboard.items.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {[0, 1].map((colIndex) => (
                    <div key={colIndex} className="space-y-2">
                      {leaderboard.items.slice(colIndex * 5, colIndex * 5 + 5).map((item, i) => {
                        const staggerIndex = colIndex * 5 + i;
                        const displayName =
                          item.person?.fullName ?? item.user?.nickname ?? "未知";
                        const jobNumber = item.person?.jobNumber?.trim();
                        return (
                          <motion.div
                            key={item.inviterId}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                              duration: 0.35,
                              delay: staggerIndex * 0.07,
                              ease: [0.25, 0.46, 0.45, 0.94],
                            }}
                          >
                            <ItemCard className="min-w-0">
                              <ItemCard.Icon>
                                <Avatar className="h-14 w-14 shrink-0" size="lg" variant="soft">
                                  {item.user?.avatarUrl ? (
                                    <Avatar.Image alt="" src={item.user.avatarUrl} />
                                  ) : null}
                                  <Avatar.Fallback className="text-lg">
                                    {displayName.charAt(0).toUpperCase()}
                                  </Avatar.Fallback>
                                </Avatar>
                              </ItemCard.Icon>
                              <ItemCard.Content className="min-w-0">
                                <ItemCard.Title className="truncate text-2xl font-medium leading-tight">
                                  {displayName}
                                </ItemCard.Title>
                                <ItemCard.Description className="mt-1 line-clamp-2 text-base text-muted-foreground">
                                  <span className="font-medium text-foreground">第 {item.rank} 名</span>
                                  {jobNumber ? (
                                    <>
                                      <span className="text-muted-foreground"> · </span>
                                      <span>{jobNumber}</span>
                                    </>
                                  ) : null}
                                </ItemCard.Description>
                              </ItemCard.Content>
                              <ItemCard.Action>
                                <div className="flex shrink-0 flex-col items-end gap-0.5 text-right">
                                  <span className="text-sm text-muted-foreground">本周邀请</span>
                                  <div className="flex items-baseline gap-1">
                                    <NumberTicker
                                      value={item.inviteCount}
                                      duration={600}
                                      decimalPlaces={0}
                                      className="text-3xl font-medium text-foreground tabular-nums"
                                    />
                                    <span className="text-lg text-muted-foreground">人</span>
                                  </div>
                                </div>
                              </ItemCard.Action>
                            </ItemCard>
                          </motion.div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  暂无排行榜数据
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
